import { useState } from 'react';
import { useAuth } from '../auth.js';
import { formatAgo, formatLocal, viewerTimeZone } from '../lib/format.js';
import {
  SOURCE_LABEL, SWITCH_DEFAULT, SWITCHES, type SwitchesResponse, type SwitchInfo, type SwitchName, type SwitchOutcome,
} from '../switches.js';
import { useSetSwitch, useSwitchesQuery, type SetSwitch } from '../use-api.js';

const onOff = (v: boolean) => (v ? 'On' : 'Off');

function LastChanged({ data, nowMs }: { data: SwitchesResponse; nowMs: number }) {
  if (data.version === 0 || !data.updatedAt) {
    return <p className="sub">Not yet set in Vinz’s config, so every switch is off (the fail-safe).</p>;
  }
  return (
    <p className="sub">
      Last changed {formatAgo(data.updatedAt, nowMs)} ({formatLocal(data.updatedAt)})
      {data.updatedBy ? ` by ${data.updatedBy}` : ''}{data.via ? ` from the ${data.via === 'cli' ? 'CLI' : 'console'}` : ''}.
    </p>
  );
}

/**
 * The switches, with Turn on / Turn off for an operator. Each row shows only what the API last returned: while a change
 * is in flight (or being re-read after an unclear answer) every button waits, and the value changes only when the
 * API's new state arrives - never a guessed value beside a stale one.
 */
export function SwitchesPanel({ data, set, nowMs, outcome, setOutcome }: {
  data: SwitchesResponse; set: SetSwitch; nowMs: number;
  outcome: SwitchOutcome | null; setOutcome: (o: SwitchOutcome | null) => void;
}) {
  const [pending, setPending] = useState<SwitchName | null>(null);

  const change = async (info: SwitchInfo, value: boolean) => {
    if (!window.confirm(`Turn ${info.label.toLowerCase()} ${value ? 'on' : 'off'}?\n\n${value ? info.on : info.off}\n\n`
      + 'It applies to every Vinz service at once and is posted to #vinz-ops.')) return;
    setPending(info.name);
    setOutcome(null);
    try {
      setOutcome(await set(info, value, data.version));
    } finally {
      setPending(null);
    }
  };

  const refs = data.probeJobWorkflowRefs;
  return (
    <section className="card">
      <div className="section-head"><h3>Switches</h3></div>
      {!data.canEdit && <p className="sub">Read-only: only a Vinz operator can change these.</p>}
      <table className="grid grid-compact">
        <thead>
          <tr>
            <th scope="col" style={{ width: '38%' }}>Switch</th>
            <th scope="col">Value</th>
            <th scope="col">Set from</th>
            <th scope="col">Default</th>
            {data.canEdit && <th scope="col" aria-label="Change" />}
          </tr>
        </thead>
        <tbody>
          {SWITCHES.map((info) => {
            const sw = data.switches[info.name];
            return (
              <tr key={info.name}>
                <td>
                  <div className="strong-medium">{info.label}</div>
                  <div className="sub">{sw.value ? info.on : info.off}</div>
                </td>
                <td><span className={`pill ${sw.value ? 'pill-green' : 'pill-neutral'}`}>{onOff(sw.value)}</span></td>
                <td>{SOURCE_LABEL[sw.source]}</td>
                <td>{onOff(SWITCH_DEFAULT)}</td>
                {data.canEdit && (
                  <td>
                    <button type="button" className={`act act-secondary${sw.value ? ' act-danger' : ''}`} disabled={pending !== null}
                      onClick={() => void change(info, !sw.value)}>
                      {pending === info.name ? 'Saving…' : `Turn ${sw.value ? 'off' : 'on'}`}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
          <tr>
            <td>
              <div className="strong-medium">Probe job workflow refs</div>
              <div className="sub">Extra refs the in-run authorizer accepts, for a pinned probe. Changed from the CLI only.</div>
            </td>
            <td>
              {refs.value.length ? refs.value.map((r) => <div key={r} className="mono">{r}</div>) : <span className="muted">None</span>}
            </td>
            <td>{SOURCE_LABEL[refs.source]}</td>
            <td>None</td>
            {data.canEdit && <td />}
          </tr>
        </tbody>
      </table>
      <LastChanged data={data} nowMs={nowMs} />
      <OutcomeLine outcome={outcome} />
    </section>
  );
}

function OutcomeLine({ outcome }: { outcome: SwitchOutcome | null }) {
  return outcome && <div role="status" className={`sub ${outcome.ok ? '' : 'tone-red'}`}>{outcome.message}</div>;
}

export function Settings() {
  const { idToken } = useAuth();
  const { data, isPending, isError, error } = useSwitchesQuery(idToken);
  const set = useSetSwitch(idToken);
  // Here, not in the panel: a failed re-read swaps the panel for the error card, and the change's outcome - often the
  // very message saying it isn't known whether it landed - must still be shown.
  const [outcome, setOutcome] = useState<SwitchOutcome | null>(null);

  if (isPending) return <div className="card">Loading settings…</div>;
  if (isError) {
    return (
      <div className="card">
        Couldn’t load settings: {error instanceof Error ? error.message : String(error)}
        <OutcomeLine outcome={outcome} />
      </div>
    );
  }
  return (
    <div>
      <div className="page-head">
        <div>
          <h2>Settings</h2>
          <p>Vinz-wide switches. Everyone can see them; only a Vinz operator can change them.</p>
        </div>
        <div className="page-head-meta">Times in {viewerTimeZone()}</div>
      </div>
      <SwitchesPanel data={data} set={set} nowMs={Date.now()} outcome={outcome} setOutcome={setOutcome} />
    </div>
  );
}
